import { ReferenceManyField } from "../fields/ReferenceManyField";
import {
  Create,
  FileInput,
  Identifier,
  RecordContextProvider,
  useGetOne,
  useGetRecordId,
  useInput,
  useListContext,
  useRecordContext,
} from "react-admin";
import { Form2 } from "../misc/Form2";
import { SaveButton2 } from "../buttons/SaveButton2";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ImageIcon from "@mui/icons-material/Image";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Document } from "../model/Document";
import { DateField } from "../fields/DateField";
import { reportError } from "../backoffice.utils";
import { Spinner } from "../misc/Spinner";
import { CSSProperties, forwardRef, useImperativeHandle, useState } from "react";
import { useQueryClient } from "react-query";
import { autovioColors } from "../misc/backofficeTheme";
import { determineMimeType } from "../utils/determineMimeType";
import { getFileNameExtension } from "../utils/getFileNameExtension";
import { studentDocumentsProvider, type DocumentsSource } from "../providers/documentsProvider.js";
import { AttachmentIcon } from "../icons/AttachmentIcon";
import { Row } from "../misc/Row";
import { useMenu } from "../hooks/useMenu";
import { grants } from "../backoffice.access_control";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { DeleteIcon } from "../icons/DeleteIcon";
import { formatFileSize } from "../utils/string";
import type { User } from "../providers/usersProvider.js";
import { basenameWithoutExtension } from "../utils/basenameWithoutExtension";
import { openDownloadWindow } from "../utils/openDownloadWindow";

export const DocumentsList = forwardRef(
  ({ style, resource }: { style?: CSSProperties; resource: DocumentsSource }, ref) => {
    const recordId = useGetRecordId();
    return (
      <div style={style}>
        <ReferenceManyField
          label=""
          resource={resource}
          reference={resource}
          target={resource === "studentDocuments" ? "studentUid" : "drivingSchoolUid"}
          sort={{ field: "createdAt", order: "DESC" }}
        >
          <_DocumentsList resource={resource} recordId={recordId} ref={ref} />
        </ReferenceManyField>
      </div>
    );
  },
);

const _DocumentsList = forwardRef(
  ({ recordId, resource }: { recordId: Identifier; resource: DocumentsSource }, ref) => {
    const { data, isLoading, total, refetch } = useListContext<Document>();
    const [state, setState] = useState<"show button" | "show form">("show button");

    useImperativeHandle(ref, function () {
      return {
        showForm: () => setState("show form"),
      };
    });

    const form = state === "show form" && (
      <_UploadDocumentForm recordId={recordId} close={() => setState("show button")} resource={resource} />
    );

    if (isLoading) {
      return <Spinner style={{ margin: "20px" }} />;
    }

    if (!data || data.length === 0 || total === 0) {
      return (
        form || (
          <>
            <Typography
              variant="body2"
              sx={{
                marginBottom: "30px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              Keine Dokumente vorhanden.
            </Typography>
          </>
        )
      );
    }
    return (
      <>
        {form}
        <List sx={{ p: 0 }}>
          {data.map((document) => (
            <RecordContextProvider key={document.id} value={document}>
              <_DocumentsListItem resource={resource} refetch={refetch} recordId={recordId} />
            </RecordContextProvider>
          ))}
        </List>
      </>
    );
  },
);

function _DocumentsListItem({
  resource,
  recordId,
  refetch,
}: {
  resource: DocumentsSource;
  recordId: Identifier;
  refetch: () => void;
}) {
  const document = useRecordContext<Document>();
  const [{ drivingSchoolId }] = useAutovioContext();
  const [showActions, setShowActions] = useState(false);
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const deletor = useDeletedBy(document);

  const deleteDocument = async () => {
    try {
      await studentDocumentsProvider.delete("", {
        id: document.id,
        meta: { document, resource, studentId: recordId, drivingSchoolId },
      });
      refetch();
      showSuccessToast("Dokument erfolgreich gelöscht.");
    } catch (error) {
      showErrorToast("Fehler beim Löschen des Dokuments.");
      reportError(`Failed to delete document ${document.id}`, error);
    }
  };
  const fileName = basenameWithoutExtension(document.fileName);

  return (
    <ListItem
      disablePadding
      sx={{
        mt: 0,
        mb: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        opacity: document.deleted ? 0.6 : 1,
      }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <ListItemButton
        sx={{ padding: 0, overflowWrap: "break-word", alignItems: "baseline", width: "100%" }}
        onClick={async () => {
          const documentWindow = openDownloadWindow();
          try {
            const downloadUrl = await document.getDownloadUrl();
            documentWindow.navigate(downloadUrl);
          } catch (error) {
            documentWindow.close();
            showErrorToast("Fehler beim Öffnen des Dokuments.");
            reportError(`Failed to get download URL for document ${document.id}`, error);
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: "30px", mt: "4px" }}>
          <Row
            sx={{
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: "action.hover",
            }}
          >
            {document.contentType.startsWith("image/") ? (
              <ImageIcon sx={{ fontSize: "16px" }} />
            ) : (
              <AttachmentIcon sx={{ "&.MuiSvgIcon-root": { width: "7px", height: "12px" } }} />
            )}
          </Row>
        </ListItemIcon>
        <ListItemText
          sx={{
            "& .MuiTypography-root": {
              fontSize: "14px",
              lineHeight: 1.29,
              fontWeight: "normal",
              color: "text.primary",
              textDecoration: document.deleted ? "line-through" : "none",
            },
          }}
          primary={
            fileName.length >= 24 ? (
              <Tooltip title={fileName}>
                <span>{`${fileName.slice(0, 24)}...`}</span>
              </Tooltip>
            ) : (
              fileName
            )
          }
          secondary={
            <>
              <DateField
                sx={{ "&.MuiTypography-root": { color: "text.secondary", fontSize: "12px" } }}
                source="createdAt"
                showTime
              />
              {document.signatureRequestState === "notSigned" && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    "&.MuiTypography-root": { color: "text.secondary", fontSize: "12px", whiteSpace: "preserve" },
                  }}
                >
                  {" • noch nicht unterschrieben"}
                </Typography>
              )}
              {document.internal && (
                <Tooltip title="Internes Dokument – für den Fahrschüler nicht sichtbar">
                  <VisibilityOffOutlinedIcon sx={{ ml: 1, fontSize: "14px", verticalAlign: "middle", opacity: 0.7 }} />
                </Tooltip>
              )}
            </>
          }
        />
      </ListItemButton>
      {document.deleted && document.deletedAt && deletor && (
        <Row color={autovioColors.red} fontSize="16px" gap={0.5} alignItems="center" mt="-4px">
          <DeleteIcon fontSize="inherit" />
          <Typography component="span" variant="body2">
            Gelöscht am {document.deletedAt.toFormat("d.M.yyyy")} von {deletor.name}
          </Typography>
        </Row>
      )}
      {(showActions || open) &&
        !document.deleted &&
        ["studentNoteAttachment", "signatureRequest", "upload"].includes(document.type) &&
        grants.has("Note:delete") && (
          <>
            <IconButton onClick={openMenu} sx={{ position: "absolute", right: 0, top: "auto", bottom: "auto" }}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={closeMenu}
              sx={(theme) => ({ zIndex: theme.zIndex.modal + 2 })}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <MenuItem
                onClick={() => {
                  closeMenu();
                  void deleteDocument();
                }}
              >
                Löschen
              </MenuItem>
            </Menu>
          </>
        )}
    </ListItem>
  );
}

function _UploadDocumentForm({
  recordId,
  close,
  resource,
}: {
  recordId: Identifier;
  close: () => void;
  resource: DocumentsSource;
}) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [files, setFiles_] = useState<Array<File>>([]);
  const [mimeTypes, setMimeTypes] = useState<Array<string>>([]);
  const setFiles = async (newFiles: Array<File>) => {
    const newMimeTypes = await Promise.all(newFiles.map(determineMimeType));
    for (let i = 0; i < newFiles.length; i++) {
      const extension = getFileNameExtension(newFiles[i].name);
      if (extension === "pdf" && newMimeTypes[i] !== "application/pdf") {
        newMimeTypes[i] = ""; // <-- this will render the name of the invalid pdf file in red color
      }
    }
    setFiles_(files.concat(newFiles));
    setMimeTypes(mimeTypes.concat(newMimeTypes));
  };
  return (
    <Create
      className="upload-document"
      title={<></>}
      sx={{
        marginTop: 0,
        marginBottom: "8px", // ... not 16px because the <ul> below this form already has a top padding of 8px
        border: "1px solid",
        borderColor: "divider",
      }}
      record={{ recordId }}
      mutationOptions={{
        onSuccess: async () => {
          // Invalidate notes queries too, because a note is added,
          // when documents are added to a student ...
          await queryClient.invalidateQueries([resource]);
          await queryClient.invalidateQueries([
            resource === "studentDocuments" ? "studentNotes" : "drivingSchoolNotes",
          ]);
          close();
        },
      }}
    >
      <Form2>
        <FileInput
          label={false}
          name="files"
          source="files"
          multiple
          options={{ onDrop: setFiles }}
          placeholder={
            <>
              {files.length === 0 && (
                <Typography component="p" sx={{ color: "text.disabled" }}>
                  Datei hier hineinziehen oder
                  <br />
                  hier klicken, um Datei auszuwählen ...
                </Typography>
              )}
              {files.map((file: File, index) => (
                <p
                  key={file.name}
                  style={{
                    marginTop: index === 0 ? "16px" : 0,
                    marginBottom: index === files.length - 1 ? "16px" : 0,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    color: file.size === 0 || !mimeTypes[index] ? theme.palette.error.main : theme.palette.text.primary,
                  }}
                >
                  {file.name} ({formatFileSize(file.size)})
                </p>
              ))}
            </>
          }
        />
        {resource === "studentDocuments" && <_InternalDocumentCheckbox />}
        <Row sx={{ p: "0 6px 6px 6px", justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={close} style={{ marginRight: "6px" }}>
            Abbrechen
          </Button>
          <SaveButton2 />
        </Row>
      </Form2>
    </Create>
  );
}

function _InternalDocumentCheckbox() {
  const { field } = useInput({ source: "internal", defaultValue: false });
  return (
    <FormControlLabel
      sx={{ ml: 0 }}
      control={<Checkbox checked={!!field.value} onChange={field.onChange} />}
      label={
        <Row sx={{ alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2">Internes Dokument</Typography>
          <Tooltip title="Interne Dokumente sind nur für Mitarbeiter sichtbar und werden dem Fahrschüler nicht angezeigt.">
            <InfoOutlinedIcon sx={{ fontSize: "16px", color: "text.secondary" }} />
          </Tooltip>
        </Row>
      }
    />
  );
}

function useDeletedBy(document: Document): User | undefined {
  const { data } = useGetOne<User>("users", { id: document.deletedById ?? "" }, { enabled: !!document.deletedById });
  return data;
}
